/**
 * @file   src\pages\InventoryConfirmation.js
 * @brief  This file is responsible for confirmation page on inventpry creation.
 * @date   June, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import usePlan from "@/hooks/usePlan";
import React, { useState, useEffect } from "react";
import ConsumerIcon from "../images/consumer-icon.svg";
import InventoryLocationMap from "../images/inventory-location-map.png";
import SchoolSmallIcon from "../images/school-icon.svg";
import NeighborhoodIcon from "../images/neighborhood-icon.svg";
import { getNiceToHaves } from "../services/commonServices";
import { convertToNumber } from "../helpers/validations";
import {
  INVENTORY_LAND,
  INVENTORY_LTR,
  INVENTORY_LTR_STR,
  INVENTORY_SELL,
  INVENTORY_SELL_LTR,
  INVENTORY_SELL_STR,
  INVENTORY_STR,
  IS_LAND,
  MEMBER_INVENTORY_EDIT_KEY,
  USER_INFO_KEY,
  INVENTORY_KEY,
  AGENT_INVENTORY_CREATE_KEY,
  AGENT_INVENTORY_EDIT_KEY,
  WANT_AD_KEY,
  USER_TYPE_AGENT,
  LONG_RENT,
  SELL,
  SHORT_RENT,
  NICE_2_HAVES_CATEGORIES_MASTER_LIST,
  NICE_2_HAVES_LOT_LAND_CATEGORIES,
} from "../constants/common";
import {
  buildInventoryMustHaveHtml,
  checkInventoryType,
  handleMapImageUpload,
} from "../helpers/inventory";
import {
  convertToCurrencySystem,
  getUserType,
  removeDecimalZeros,
} from "../helpers/common";
import { saveInventoryStrengthenData } from "../services/inventoryService";

import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../helpers/common";
import { useNavigate, useLocation } from "react-router-dom";
import { saveMemberInventoryData } from "../services/inventoryService";
import { pageURLs } from "@/constants";
import PlaceHolderIcon from "../images/dpdown-placeholder-icon.svg";
import TimelineIcon from "../images/timeline-icon.svg";
import { deleteFile } from "../services/fileUploadService";
import { useRef } from "react";
import PhotoGallery from "../components/Common/PhotoGallery";
import MapboxComponent from "../components/Common/MapboxComponent";
import { PageWrapper } from "@/components/PageWrapper";
import useLoading from "@/hooks/useLoading";

const LOADING_KEY = "saveInventory";

const InventoryConfirmation = (props) => {

  const navigate = useNavigate();
  const location = useLocation();
  const pageTopRef = useRef(null);
  const inventoryId = location.state && location?.state?.["inventoryId"];
  const sessionKey =
    location.state && location?.state?.["inventoryId"]
      ? location?.state?.["isAgentCreated"]
        ? AGENT_INVENTORY_EDIT_KEY
        : MEMBER_INVENTORY_EDIT_KEY
      : location?.state?.["isAgentCreated"]
        ? AGENT_INVENTORY_CREATE_KEY
        : MEMBER_INVENTORY_EDIT_KEY;


  const wantAdId = location.state && location.state["RedirectwantAdUuid"];
  const [formValues, setFormValues] = useState(getLocalStorage(sessionKey));
  const propertyType = convertToNumber(_.get(formValues, "prprtyType", ""));
  const proprtyPurpose = _.get(formValues, "proprtyPurpose", []);
  const LTRAskngPrce = _.get(formValues, "LTRAskngPrce", "");
  const STRAskngPrce = _.get(formValues, "STRAskngPrce", "");
  const lat = _.get(formValues, "lat", null);
  const lng = _.get(formValues, "lng", null);
  const timeLineTxt =
    _.get(formValues, "timeLineTxt", "") || _.get(formValues, "tlText", "");
  const description = _.get(formValues, "invtryDetails", "");

  const nice2Have = _.get(formValues, "nice2Have", []);
  const agent = _.get(formValues, "isAgentCreated", "");
  const userType = getUserType();
  const isEdit = _.get(formValues, "inventoryId", "") ? true : false;

  const [niceToHavesList, setNiceToHavesList] = useState([]);
  // const invType = checkInventoryType(proprtyPurpose);
  const invType = INVENTORY_SELL_STR;
  const typeBasedOnProperty =
    propertyType === IS_LAND ? INVENTORY_LAND : invType;

  const plan = usePlan();
  const isPremiumUser = plan.loaded ? plan.hasPaidPlan : null;

  const isAgent = userType === USER_TYPE_AGENT ? true : false;
  const { startLoading, stopLoading } = useLoading();



  useEffect(() => {
    if (formValues == null) {
      navigate(pageURLs.home);
    }
  }, [formValues]);

  let titleText = "";
  switch (invType) {
    case INVENTORY_SELL:
      titleText = " selling";
      break;
    case INVENTORY_LTR:
      titleText = " renting";
      break;
    case INVENTORY_STR:
      titleText = " renting";
      break;
    case INVENTORY_LTR_STR:
      titleText = " renting";
      break;
    case INVENTORY_SELL_LTR:
      titleText = " selling, renting";
      break;
    case INVENTORY_SELL_STR:
      titleText = " selling, renting";
      break;
    default:
      titleText = " selling, renting";
      break;
  }

  useEffect(() => {
    window.scrollTo(0, pageTopRef.current.offsetTop); // focus to the top section
    getNiceToHaves(typeBasedOnProperty)
      .then((response) => {
        if (response.errorCode === 0 && _.get(response, "result", null)) {
          const list = _.get(response, "result", null);
          setNiceToHavesList(list);
        }
      })
      .catch((err) => {
        console.log("Error", err);
      });
  }, []);
  /**
   * To load selected must haves
   * @returns
   */
  const loadDetails = () => {
    return buildInventoryMustHaveHtml({
      typeBasedOnProperty: typeBasedOnProperty,
      formValues: formValues,
    });
  };
  /**
   * To load selected nice to haves
   * @param {*} section
   * @returns
   */
  const loadEachNiceToHave = (section) => {
    const selectedItems =
      nice2Have && nice2Have.filter((item) => item.tab === section);
    const masterArray = _.get(niceToHavesList, section, []);
    if (selectedItems.length) {
      return loadNiceToHaveList(masterArray, selectedItems, section.charAt(0));
    } else {
      return "";
    }
  };

  const finalNiceToHaveList = () => {
    const niceToHaveMasterArray = typeBasedOnProperty === INVENTORY_LAND ?
      NICE_2_HAVES_LOT_LAND_CATEGORIES.map((item) => item.category) :
      NICE_2_HAVES_CATEGORIES_MASTER_LIST.map((item) => item.category);

    const finalList =
      niceToHaveMasterArray &&
      niceToHaveMasterArray.map((nh) => {
        const list = loadEachNiceToHave(nh);
        if (list.length) {
          return list;
        } else {
          return "";
        }
      });
    return finalList;
  };

  const loadNiceToHaveList = (masterList, selectedItems, uniqueKey) => {
    return (
      selectedItems &&
      selectedItems.map((nt) => {
        const id = _.get(nt, "id");
        const masterItem =
          masterList && masterList.filter((item) => item.Id === id);
        const itemText = _.get(masterItem[0], "Text", "");
        const icon = _.get(masterItem[0], "Icon", "");
        return (
          <div className="nicetohaves-tag" key={id + uniqueKey}>
            <div className="nicetohaves-tag-icon">
              <img
                src={
                  icon
                    ? (() => {
                      try {
                        return require("../images/" + icon);
                      } catch (e) {
                        return PlaceHolderIcon;
                      }
                    })()
                    : PlaceHolderIcon
                }
                style={{ width: 16, height: 16 }}
              />
            </div>
            <span>{itemText}</span>
          </div>
        );
      })
    );
  };
  /**
   * To handle cancel post inventory
   */
  const handleCancelClick = () => {
    if (
      confirm("Leave before saving these property details?")
    ) {
      removeLocalStorage(sessionKey);
      _.get(formValues, "Inv_Id", "")
        ? navigate(pageURLs.agentInventoryListing)
        : navigate(pageURLs.home);
    }
  };
  /**
   * To handle add inventory details
   * @param {*} isAddInventry
   */
  const handlePostClick = (isAddInventry) => {
    const isConfirmAll = _.get(formValues, "isConfirmAll", false);
    saveMemberInventory(isAddInventry);
  };
  /**
   * To save inventory details
   * @param {*} isAddInventry
   */
  const saveMemberInventory = async (isAddInventry) => {
    startLoading(LOADING_KEY);

    let requestPayLoad = formValues;
    if (!requestPayLoad) {
      requestPayLoad = getLocalStorage(sessionKey);
    }

    const mapImageUrl = await handleMapImageUpload({
      lat: _.get(formValues, "lat", ""),
      lng: _.get(formValues, "lng", ""),
      mapZoom: _.get(formValues, "mapZoom"),
      s3ImageFolder: _.get(formValues, "s3ImageFolder", ""),
    });
    if (mapImageUrl) {
      requestPayLoad.mapImg = mapImageUrl;
    }

    requestPayLoad.cmpldStep = _.get(formValues, "actvStep", 1);
    if (isAgent && !isPremiumUser && !requestPayLoad.clientId) {
      requestPayLoad.isAgentAsSeller = true;
    } else if (requestPayLoad.clientId) {
      requestPayLoad.isAgentAsSeller = false;
    }
    if (isAddInventry) {
      requestPayLoad.IsSavedDraft = false;
      requestPayLoad.IsPublished = true;
      delete requestPayLoad.tlText;
      delete requestPayLoad.prptyTypeTxt;
      delete requestPayLoad.step6Errors;
    } else {
      requestPayLoad.IsSavedDraft = true;
      requestPayLoad.IsPublished = false;
    }

    saveMemberInventoryData(requestPayLoad)
      .then((response) => {
        const responseResult = _.get(response, "result", null);

        if (response.errorCode === 0 && responseResult) {
          if (isAddInventry) {
            if (!inventoryId) {
              // for setting the match tab client data
              setLocalStorage("MatchSelectedClient", {
                FirstName: requestPayLoad.clientName,
                LastName: requestPayLoad.clientName,
                AgentAsSeller: requestPayLoad.isAgentCreated,
                label: requestPayLoad.clientName,
                value: requestPayLoad.clientId,
              });

              let propertyPurpose = _.get(requestPayLoad, "proprtyPurpose", []);

              setLocalStorage("selectedPropertyId", {
                Inv_Id: _.get(responseResult, "inventoryId", ""),
                Location: requestPayLoad.locAddress,
                City: requestPayLoad.city,
                Is_LongTerm_Renter: _.includes(propertyPurpose, LONG_RENT),
                Is_Seller: _.includes(propertyPurpose, SELL),
                Is_ShortTerm_Renter: _.includes(propertyPurpose, SHORT_RENT),
                State: requestPayLoad.state,
                photo: requestPayLoad.photos && requestPayLoad.photos[0],
              });
            }
          }
          if (
            requestPayLoad.apprOnFile ||
            requestPayLoad.insReport ||
            requestPayLoad.sellerFin ||
            requestPayLoad.floorPlan ||
            requestPayLoad.floodCert ||
            requestPayLoad.tlText ||
            !requestPayLoad.apprOnFile ||
            !requestPayLoad.insReport ||
            !requestPayLoad.sellerFin ||
            !requestPayLoad.floorPlan ||
            !requestPayLoad.floodCert ||
            !requestPayLoad.tlText
          ) {
            saveStrengthenData(responseResult.inventoryId, isAddInventry);
          } else {
            stopLoading(LOADING_KEY);
            removeLocalStorage(sessionKey);
            removeLocalStorage("PhotosAfterDelete");
            if (isAddInventry) {
              if (_.get(formValues, "isFromIntroduction", "")) {
                setLocalStorage(WANT_AD_KEY, {
                  wantAdId: _.get(formValues, "wantAdId", ""),
                  isFromHomeFeed: true,
                });
                removeLocalStorage(sessionKey);
                navigate(`${pageURLs.wantAdDetails}/${wantAdId}`);
              } else {
                let clientName = _.get(formValues, "clientName", "")
                  ? _.get(formValues, "clientName", "")
                  : _.get(formValues, "clientData.label", "");
                let isAgentAsSeller = _.get(formValues, "isAgentAsSeller", "");
                removeLocalStorage(sessionKey);
                const newInventoryId = _.get(responseResult, "inventoryId", "");
                navigate(`${pageURLs.inventoryDetails}/${newInventoryId}`, {
                  state: {
                    showSuccessModal: true,
                    isAgentCreated: agent,
                    clientName: clientName,
                    isAgentAsSeller: isAgentAsSeller,
                    isEdit: isEdit,
                  },
                });
              }
            } else {
              removeLocalStorage(sessionKey);
              removeLocalStorage("PhotosAfterDelete");
              navigate(pageURLs.agentInventoryListing);
            }
          }
        } else {
          console.log("Something went wrong");
          stopLoading(LOADING_KEY);
        }
      })
      .catch((err) => {
        console.log("Error", err);
        stopLoading(LOADING_KEY);
      });
  };
  /**
   * To save strengthen inventory data
   * @param {*} InventoryId
   * @param {*} isAddInventry
   */
  const saveStrengthenData = async (InventoryId, isAddInventry) => {
    let requestPayLoad = formValues;
    if (!requestPayLoad) {
      requestPayLoad = getLocalStorage(sessionKey);
    }
    const userDetails = getLocalStorage(USER_INFO_KEY);
    const userId = _.get(userDetails, "userInfo.userId", null);
    requestPayLoad.inventoryId = InventoryId;
    requestPayLoad.isStrengthen = true;
    await saveInventoryStrengthenData(requestPayLoad)
      .then((response) => {
        const responseResult = _.get(response, "result", null);
        stopLoading(LOADING_KEY);
        if (response.errorCode === 0 && responseResult) {
          if (isAddInventry) {
            if (_.get(formValues, "isFromIntroduction", "")) {
              setLocalStorage(WANT_AD_KEY, {
                wantAdId: _.get(formValues, "wantAdId", ""),
                isFromHomeFeed: true,
              });
              removeLocalStorage(sessionKey);
              navigate(`${pageURLs.wantAdDetails}/${wantAdId}`);
            } else {
              let clientName = _.get(formValues, "clientName", "")
                ? _.get(formValues, "clientName", "")
                : _.get(formValues, "clientData.label", "");
              let isAgentAsSeller = _.get(formValues, "isAgentAsSeller", "");
              removeLocalStorage(sessionKey);
              removeLocalStorage("PhotosAfterDelete");
              const newStrengthenInventoryId = _.get(responseResult, "inventoryId", "");
              navigate(`${pageURLs.inventoryDetails}/${newStrengthenInventoryId}`, {
                state: {
                  showSuccessModal: true,
                  isAgentCreated: agent,
                  clientName: clientName,
                  isAgentAsSeller: isAgentAsSeller,
                  isEdit: isEdit,
                },
              });
            }
          } else {
            removeLocalStorage(sessionKey);
            removeLocalStorage("PhotosAfterDelete");
            navigate(pageURLs.agentInventoryListing);
          }
        }
      })
      .catch((err) => {
        console.log("Error", err);
        stopLoading(LOADING_KEY);
      });
  };

  const isNiceToHaveExist = nice2Have.length > 0;
  const photos = _.get(formValues, "photos", []);
  const highSchool = _.get(formValues, "highSchool", null);
  const neighborHood = _.get(formValues, "neighborHood", null);

  const mapImg = _.get(formValues, "mapImg", null);
  const _photos =
    photos.length > 0 ? photos : mapImg ? [{ image: mapImg }] : [];

  const hasAdditionalDetails = timeLineTxt || formValues?.apprOnFile || formValues?.insReport || formValues?.sellerFin || formValues?.floorPlan || formValues?.floodCert || formValues?.assMortgage

  return (
    <PageWrapper ref={pageTopRef}>
      <div className="m-0 pb-4">
        <div className="main-content p-0 agent-no-login-content">
          {/* To do when admin login is true when we use (m-2) */}
          {/* <div className="row m-2"> */}
          <div className="row m-0">
            <div className="col-md-12">
              <div className="ready-to-post-wrap mw-724">
                <div className="ready-to-post-top d-sm-flex justify-content-between mt-3">
                  <div>
                    {inventoryId ? (
                      <h2>Ready to save changes?</h2>
                    ) : (
                      <h2>Ready to save your property?</h2>
                    )}

                    {isPremiumUser && (
                      <>
                        {_.get(formValues, "isAgentCreated", "") == true &&
                          (_.get(formValues, "isAgentAsSeller", "") ==
                            "true" ||
                            _.get(formValues, "isAgentAsSeller", "") == 1 ? (
                            <></>
                          ) : (
                            <h6>
                              Client:{" "}
                              {_.get(formValues, "clientName", "")
                                ? _.get(formValues, "clientName", "")
                                : _.get(
                                  formValues,
                                  "clientData.label",
                                  "",
                                )}{" "}
                              <span>(Only visible to you)</span>
                            </h6>
                          ))}
                      </>
                    )}
                  </div>
                  <div className="d-block mt-3 mt-sm-0">
                    {inventoryId ? (
                      <button
                        className="btn block-link-btn me-2"
                        onClick={() => handleCancelClick()}
                      >
                        Cancel
                      </button>
                    ) : (
                      <button
                        className="btn block-link-btn me-2"
                        onClick={() => handlePostClick(false)}
                      >
                        Save Draft
                      </button>
                    )}

                    <button
                      className="btn btn-primary"
                      onClick={() => handlePostClick(true)}
                    >
                      {" "}
                      {inventoryId ? "Save" : "Confirm"}
                    </button>
                  </div>
                </div>

                <div className="inventory-confirmation-wrap">
                  <div className="confirmation-top-sec d-md-flex justify-content-between">
                    <div>
                      <div className="confirm-sub-title mb-1">
                        I'm open to:{" "}
                        <span className="want-ad-tag align-middle green-tag-bg mb-0 f-size-10">
                          {titleText}
                        </span>
                      </div>
                      <h2 className="long-txt-fix">
                        {_.get(formValues, "locAddress", "")}
                      </h2>
                    </div>

                    <button
                      type="button"
                      className="btn btn-outline-primary align-self-start"
                      onClick={() =>
                        _.get(formValues, "isAgentCreated", "")
                          ? navigate(pageURLs.agentCreateInventory, {
                            state: {
                              inventoryId: _.get(
                                formValues,
                                "inventoryId",
                                "",
                              ),
                            },
                          })
                          : navigate(pageURLs.memberEditInventory)
                      }
                    >
                      Edit
                    </button>
                  </div>

                  <div className="musthave-wrap">
                    <h2>Details</h2>
                    {loadDetails()}
                  </div>

                  <div className={`Inventory-Location-wrap pb-32${hasAdditionalDetails ? "" : " border-0"}`}>
                    <h2>Location</h2>
                    <div className="Inventory-Location-map-wrap mb-3 h-264">
                      <MapboxComponent center={[lng, lat]} />
                    </div>
                    <ul>
                      {highSchool && (
                        <li>
                          <span className="Inventory-icn-wrap">
                            <img src={SchoolSmallIcon} />
                          </span>
                          {highSchool}
                        </li>
                      )}
                      {neighborHood && (
                        <li>
                          <span className="Inventory-icn-wrap">
                            <img src={NeighborhoodIcon} />
                          </span>
                          {neighborHood}
                        </li>
                      )}
                    </ul>
                  </div>
                  {isNiceToHaveExist ? (
                    <div className="nicetohaves-wrap">
                      <h2>Features</h2>
                      <div className="nicetohaves-inner-wrap">
                        {finalNiceToHaveList()}
                      </div>
                    </div>
                  ) : (
                    ""
                  )}

                  {description && (
                    <div className="additional-details-wrap">
                      <h2>Description</h2>
                      <p className="long-txt-fix">{description}</p>
                    </div>
                  )}
                  {hasAdditionalDetails && (
                    <div className="buyer-info">
                      <h2>Additional Details</h2>
                      <ul>
                        {timeLineTxt && (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={TimelineIcon} />
                            </span>
                            Timeline: {timeLineTxt}
                          </li>
                        )}
                        {_.get(formValues, "apprOnFile", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Appraisal on file
                          </li>
                        ) : (
                          ""
                        )}

                        {_.get(formValues, "insReport", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Inspection report on file
                          </li>
                        ) : (
                          ""
                        )}
                        {_.get(formValues, "sellerFin", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Seller finance available
                          </li>
                        ) : (
                          ""
                        )}

                        {_.get(formValues, "floorPlan", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Floor plan on file
                          </li>
                        ) : (
                          ""
                        )}
                        {_.get(formValues, "floodCert", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Flood certificate available
                          </li>
                        ) : (
                          ""
                        )}
                        {_.get(formValues, "assMortgage", false) ? (
                          <li>
                            <span className="list-icn-wrap">
                              <img src={ConsumerIcon} />
                            </span>
                            Assumable mortgage
                          </li>
                        ) : (
                          ""
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PageWrapper>
  );
};

export default InventoryConfirmation;
